import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Box, Typography, Grid, Button, CircularProgress, Card, CardContent, Stack, IconButton, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Alert } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import { DynamicFieldAdvanced } from "../../../../../../components/common/input/DynamicFieldAdvanced";
import { useDispatch, useSelector } from "react-redux";
import { setJoiningForms, setStepCompletion } from "../../../../../../store/basicDetailsSlice";
import { saveEmployeeJoiningForms } from "../../../../../../api/employeeApi";
import { useNotification } from "../../../../../../hooks/useNotification";
import { getOnboardingTemplatesByEntity } from "../../../../../../api/onboardingApi";

const JoiningForms = ({ onNext, onBack, employeeId }) => {
  const dispatch = useDispatch();
  const { showNotification, Notification } = useNotification();
  const rawJoiningForms = useSelector((state) => state.basicDetails.joiningForms);
  const joiningForms = useMemo(() => rawJoiningForms || {}, [rawJoiningForms]);
  const compensation = useSelector((state) => state.basicDetails.compensation) || {};
  const entityId = useSelector((state) => state.basicDetails.formData?.entity) || null;
  const hasPF = compensation.hasExistingPF === true || compensation.hasExistingPF === 'true' || compensation.hasExistingPF === 'yes' || (compensation.hasExistingPF === undefined || compensation.hasExistingPF === "" || compensation.hasExistingPF === null ? true : compensation.hasExistingPF !== 'no');

  const [formData, setFormData] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const fileInputRef = useRef(null);

  const joiningFormFields = useMemo(() => {
    const activeTemplates = templates.filter(t => t.isActive);
    const list = [];
    activeTemplates.forEach(t => {
      if (!hasPF && (t.templateName === 'form11' || t.templateName === 'pfForm2')) {
        return;
      }
      let isRequired = true;
      if (t.templateName === 'esicForm') {
        isRequired = false;
      }
      list.push({
        name: t.templateName,
        label: t.label || t.templateName,
        required: isRequired,
        fileUrl: t.fileUrl,
        description: t.description || `Please download and upload the signed ${t.label || t.templateName}.`,
        isNomineeRequired: t.isNomineeRequired === true || t.isNomineeRequired === 'true'
      });
    });

    // Add Form 12BB conditionally if the user uploaded it and it's not already in list
    const hasForm12BB = !!joiningForms['Form 12BB'] && joiningForms['Form 12BB'].length > 0;
    if (hasForm12BB && !list.some(item => item.name === 'Form 12BB')) {
      list.push({
        name: "Form 12BB",
        label: "Form 12BB (Tax Declaration)",
        required: true,
        accept: ".pdf",
        formats: "pdf",
        description: "Please download, fill out, and upload your Form 12BB for Tax Declaration."
      });
    }

    // Add Declaration of Non-PF conditionally if the user uploaded it, and it's not already in list
    const hasNonPfDoc = !!joiningForms['Declaration of Non-PF'] && joiningForms['Declaration of Non-PF'].length > 0;
    if (hasNonPfDoc && !list.some(item => item.name === 'Declaration of Non-PF')) {
      list.push({
        name: "Declaration of Non-PF",
        label: "Declaration of Non-PF",
        required: true,
        accept: ".pdf",
        formats: "pdf",
        description: "Please download, fill out, and upload your Declaration of Non-PF."
      });
    }

    return list;
  }, [templates, hasPF, joiningForms]);

  const formDataStore = useSelector((state) => state.basicDetails.formData) || {};
  const [nominees, setNominees] = useState([]);

  const needsNominees = useMemo(() => {
    return joiningFormFields.some(f => f.isNomineeRequired);
  }, [joiningFormFields]);

  const hasLoadedNominees = useRef(false);
  useEffect(() => {
    if (!hasLoadedNominees.current && formDataStore.nominees && Array.isArray(formDataStore.nominees)) {
      const esicNominees = formDataStore.nominees.filter(n => n.nomineeType === 'ESIC Nominee');
      if (esicNominees.length > 0) {
        const mappedEsicNominees = esicNominees.map(n => ({
          ...n,
          nomineeDob: n.nomineeDob ? n.nomineeDob.split('T')[0] : "",
          aadhaarFrontPath: n.aadhaarFrontPath || "",
          aadhaarBackPath: n.aadhaarBackPath || ""
        }));
        setNominees(mappedEsicNominees);
      }
      hasLoadedNominees.current = true;
    }
  }, [formDataStore.nominees]);

  useEffect(() => {
    if (needsNominees && nominees.length === 0) {
      setNominees([
        {
          nomineeName: "",
          contactNo: "",
          nomineeDob: "",
          relation: "",
          aadhaarNumber: "",
          nomineeType: "ESIC Nominee"
        }
      ]);
    }
  }, [needsNominees, nominees.length]);

  // Fetch onboarding templates based on employee's entity
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await getOnboardingTemplatesByEntity(entityId || 'null');
        if (response.success) {
          setTemplates(response.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch onboarding templates:", err);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [entityId]);

  // Sync joiningForms from Redux when loaded/updated
  useEffect(() => {
    if (joiningForms && Object.keys(joiningForms).length > 0) {
      setFormData(prev => {
        const next = { ...prev };
        let updated = false;
        Object.keys(joiningForms).forEach(key => {
          if (joiningForms[key] !== prev[key]) {
            next[key] = joiningForms[key];
            updated = true;
          }
        });
        return updated ? next : prev;
      });
    }
  }, [joiningForms]);


  const handleFileChange = useCallback((name, files) => {
    setFormData(prev => ({
      ...prev,
      [name]: files
    }));
    dispatch(setJoiningForms({ [name]: files }));
  }, [dispatch]);

  const handleFileRemove = useCallback((name, index) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name]?.filter((_, i) => i !== index) || []
    }));
    dispatch(setJoiningForms({
      [name]: formData[name]?.filter((_, i) => i !== index) || []
    }));
  }, [formData, dispatch]);

  const handleFileChoose = useCallback((fieldName) => {
    setCurrentField(fieldName);
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((event) => {
    const files = Array.from(event.target.files);
    if (currentField) {
      handleFileChange(currentField, files);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
    setCurrentField(null);
  }, [handleFileChange, currentField]);

  const handleNomineeChange = useCallback((index, field, value) => {
    const updatedNominees = [...nominees];
    updatedNominees[index] = { ...updatedNominees[index], [field]: value };
    setNominees(updatedNominees);

    // Clear field-specific errors
    setErrors(prev => {
      const next = { ...prev };
      delete next[`nomineeName_${index}`];
      delete next[`nomineeRelation_${index}`];
      delete next[`nomineeContact_${index}`];
      delete next[`nomineeDob_${index}`];
      delete next[`nomineeAadhaarNumber_${index}`];
      delete next[`nomineeAadhaarFront_${index}`];
      delete next[`nomineeAadhaarBack_${index}`];
      return next;
    });
  }, [nominees]);

  const addNominee = useCallback(() => {
    setNominees(prev => [
      ...prev,
      {
        nomineeName: "",
        contactNo: "",
        nomineeDob: "",
        relation: "",
        aadhaarNumber: "",
        nomineeType: "ESIC Nominee"
      }
    ]);
  }, []);

  const removeNominee = useCallback((idx) => {
    setNominees(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate required files
    joiningFormFields.forEach(field => {
      if (field.required && (!formData[field.name] || formData[field.name].length === 0)) {
        newErrors[field.name] = `${field.label} is required`;
      }
    });

    if (needsNominees) {
      nominees.forEach((n, idx) => {
        if (!n.nomineeName?.trim()) {
          newErrors[`nomineeName_${idx}`] = "Nominee name is required";
        }
        if (!n.relation) {
          newErrors[`nomineeRelation_${idx}`] = "Relationship is required";
        }
        if (!n.contactNo?.trim()) {
          newErrors[`nomineeContact_${idx}`] = "Contact number is required";
        } else if (!/^\d{10}$/.test(n.contactNo.replace(/\D/g, ''))) {
          newErrors[`nomineeContact_${idx}`] = "Contact number must be 10 digits";
        }
        if (!n.nomineeDob) {
          newErrors[`nomineeDob_${idx}`] = "Date of birth is required";
        } else if (new Date(n.nomineeDob) > new Date()) {
          newErrors[`nomineeDob_${idx}`] = "DOB cannot be in the future";
        }
        if (n.aadhaarNumber && !/^\d{12}$/.test(n.aadhaarNumber.replace(/\D/g, ''))) {
          newErrors[`nomineeAadhaarNumber_${idx}`] = "Aadhaar must be 12 digits";
        }
        if (!n.aadhaarFrontFile && !n.aadhaarFrontPath) {
          newErrors[`nomineeAadhaarFront_${idx}`] = "Aadhaar front is required";
        }
        if (!n.aadhaarBackFile && !n.aadhaarBackPath) {
          newErrors[`nomineeAadhaarBack_${idx}`] = "Aadhaar back is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, joiningFormFields, needsNominees, nominees]);

  const handleNext = async () => {
    // Mark all required fields as touched
    const touchedFields = {};
    joiningFormFields.forEach(field => {
      if (field.required) {
        touchedFields[field.name] = true;
      }
    });

    if (needsNominees) {
      nominees.forEach((_, idx) => {
        touchedFields[`nomineeName_${idx}`] = true;
        touchedFields[`nomineeRelation_${idx}`] = true;
        touchedFields[`nomineeContact_${idx}`] = true;
        touchedFields[`nomineeDob_${idx}`] = true;
        touchedFields[`nomineeAadhaarFront_${idx}`] = true;
        touchedFields[`nomineeAadhaarBack_${idx}`] = true;
      });
    }

    setTouched(touchedFields);

    const isVal = validateForm();
    if (!isVal) {
      const missingFields = joiningFormFields
        .filter(field => field.required && (!formData[field.name] || formData[field.name].length === 0))
        .map(field => field.label);
      if (missingFields.length > 0) {
        showNotification(`Please upload the following required documents: ${missingFields.join(", ")}`, "error");
      } else if (needsNominees) {
        showNotification("Please check the form and fill in all ESIC nominee details correctly.", "error");
      }
      return;
    }

    if (!employeeId) {
      showNotification("Employee ID is missing", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const submissionData = new FormData();

      // Add all form files
      joiningFormFields.forEach(field => {
        if (formData[field.name] && formData[field.name].length > 0) {
          const file = formData[field.name][0];
          if (file instanceof File) {
            submissionData.append(field.name, file);
          }
        }
      });

      if (needsNominees) {
        const sanitizedNominees = nominees.map(n => ({
          ...n,
          nomineeDob: n.nomineeDob || null,
          aadhaarFrontFile: undefined,
          aadhaarBackFile: undefined
        }));
        submissionData.append('nomineeData', JSON.stringify(sanitizedNominees));

        nominees.forEach((n, index) => {
          if (n.aadhaarFrontFile) {
            submissionData.append(`nominee_${index}_aadhaarFront`, n.aadhaarFrontFile);
          }
          if (n.aadhaarBackFile) {
            submissionData.append(`nominee_${index}_aadhaarBack`, n.aadhaarBackFile);
          }
        });
      }

      await saveEmployeeJoiningForms(employeeId, submissionData);
      dispatch(setStepCompletion({ step: 'joiningForms', isCompleted: true }));

      // Mark newly uploaded files as existing so they don't get re-uploaded next time
      const updatedFormData = { ...formData };
      joiningFormFields.forEach(field => {
        if (formData[field.name] && formData[field.name].length > 0) {
          const file = formData[field.name][0];
          if (file instanceof File) {
            updatedFormData[field.name] = [{
              name: file.name,
              url: URL.createObjectURL(file), // Provide a local preview URL
              isExisting: true
            }];
          }
        }
      });
      dispatch(setJoiningForms(updatedFormData));
      setFormData(updatedFormData);
      setErrors({});
      setTouched({});

      showNotification("Joining forms uploaded successfully", "success");

      if (onNext) {
        onNext(formData);
      }
    } catch (error) {
      console.error("Error saving joining forms:", error);
      showNotification(error.message || "Failed to save joining forms", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = useCallback(() => {
    if (onNext) {
      onNext();
    }
  }, [onNext]);

  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const currentFieldObj = useMemo(() => {
    return joiningFormFields.find(f => f.name === currentField);
  }, [joiningFormFields, currentField]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: '#F5EEFC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <DescriptionOutlinedIcon sx={{ color: '#7522D1', fontSize: 18 }} />
        </Box>
        Joining Forms & Documents
      </Typography>

      <Typography variant="body2" sx={{ mb: 4, color: "#6B7280", fontWeight: 600 }}>
        Please upload all required joining forms and documents. Forms should be in PDF format, and the passport picture should be in image format (JPG, PNG).
      </Typography>

        {loadingTemplates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={40} sx={{ color: '#7522D1' }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {joiningFormFields.map((field) => (
              <Grid item size={{ xs: 12, md: 6 }} key={field.name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700, fontSize: '0.825rem' }}>
                    {field.label} {field.required && <span style={{ color: '#EF4444' }}>*</span>}
                  </Typography>
                  {field.fileUrl && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const response = await fetch(`/api/onboarding/proxy-download?url=${encodeURIComponent(field.fileUrl)}`);
                          if (!response.ok) throw new Error('Proxy failed');
                          const blob = await response.blob();
                          const link = document.createElement('a');
                          link.href = window.URL.createObjectURL(blob);
                          link.download = `${field.label || field.name}_Template.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } catch (err) {
                          window.open(field.fileUrl, '_blank');
                        }
                      }}
                      sx={{
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        color: '#7522D1',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        '&:hover': { background: 'none', textDecoration: 'underline' }
                      }}
                    >
                      Download Template
                    </Button>
                  )}
                </Box>
                <DynamicFieldAdvanced
                  type="file"
                  name={field.name}
                  value=""
                  onChange={(name, files) => handleFileChange(name, files)}
                  files={formData[field.name] || []}
                  onFileRemove={(index) => handleFileRemove(field.name, index)}
                  onFileChoose={() => handleFileChoose(field.name)}
                  accept={field.accept || ".pdf"}
                  maxSize="4MB"
                  supportedFormats={field.formats || "pdf"}
                  // required={field.required}
                  error={touched[field.name] && !!errors[field.name]}
                  errorText={touched[field.name] ? errors[field.name] : ""}
                  sx={{ maxWidth: "fit-content !important" }}
                />
              </Grid>
            ))}
          </Grid>
        )}

      {needsNominees && (
        <Box sx={{ mt: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 5,
              border: '1px solid #E2E8F0',
              overflow: 'hidden',
              boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.03)'
            }}
          >
            <Box
              sx={{
                p: 3,
                bgcolor: '#FBF9FF',
                borderBottom: `1px solid #F1F5F9`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha('#7522D1', 0.1),
                    color: '#7522D1',
                    display: 'flex'
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={800} color="#1E293B" sx={{ lineHeight: 1.2 }}>
                    ESIC Nominee Details
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Please provide nominee information as required by ESIC regulations.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                onClick={addNominee}
                sx={{
                  borderRadius: 3,
                  textTransform: 'none',
                  bgcolor: '#7522D1',
                  boxShadow: '0 4px 12px rgba(117, 34, 209, 0.25)',
                  px: 3,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#5a1aa8',
                    boxShadow: '0 6px 16px rgba(117, 34, 209, 0.35)',
                  }
                }}
              >
                Add Nominee
              </Button>
            </Box>
            <CardContent sx={{ p: { xs: 3, md: 4 }, bgcolor: '#FAF8FF' }}>
              {errors.nominees && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 4,
                    borderRadius: 3,
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    fontWeight: 500
                  }}
                >
                  {errors.nominees}
                </Alert>
              )}

              {nominees.map((nominee, idx) => (
                <Card
                  key={nominee.id || idx}
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    mb: idx === nominees.length - 1 ? 0 : 4,
                    borderRadius: 4,
                    border: '1px solid #E2E8F0',
                    bgcolor: '#FFFFFF',
                    position: 'relative',
                    transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                      borderColor: '#7522D1'
                    }
                  }}
                >
                  {nominees.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeNominee(idx)}
                      sx={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        bgcolor: '#FFF5F5',
                        border: '1px solid #FED7D7',
                        color: '#E53E3E',
                        '&:hover': {
                          bgcolor: '#FED7D7',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease',
                        width: 36,
                        height: 36
                      }}
                      size="small"
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}

                  {/* Header info with nominee avatar */}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                    <Avatar
                      sx={{
                        bgcolor: alpha('#7522D1', 0.1),
                        color: '#7522D1',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        width: 36,
                        height: 36,
                        border: '2px solid #FFFFFF',
                        boxShadow: '0 2px 8px rgba(117,34,209,0.15)'
                      }}
                    >
                      {idx + 1}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800} color="#1E293B" sx={{ lineHeight: 1 }}>
                        Nominee Details #{idx + 1}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {nominee.relation ? `${nominee.relation} Relation` : 'Specify relationship below'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Nominee Name (As per Aadhaar)"
                        size="small"
                        fullWidth
                        value={nominee.nomineeName || ''}
                        onChange={(e) => handleNomineeChange(idx, 'nomineeName', e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50))}
                        error={touched[`nomineeName_${idx}`] && !!errors[`nomineeName_${idx}`]}
                        helperText={touched[`nomineeName_${idx}`] ? errors[`nomineeName_${idx}`] : "Enter nominee's full name"}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#7522D1' },
                            '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Contact Number"
                        size="small"
                        fullWidth
                        value={nominee.contactNo || ''}
                        onChange={(e) => handleNomineeChange(idx, 'contactNo', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        error={touched[`nomineeContact_${idx}`] && !!errors[`nomineeContact_${idx}`]}
                        helperText={touched[`nomineeContact_${idx}`] ? errors[`nomineeContact_${idx}`] : "Enter 10-digit mobile number"}
                        inputProps={{ maxLength: 10 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#7522D1' },
                            '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Date of Birth"
                        size="small"
                        type="date"
                        fullWidth
                        value={nominee.nomineeDob || ''}
                        onChange={(e) => handleNomineeChange(idx, 'nomineeDob', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={touched[`nomineeDob_${idx}`] && !!errors[`nomineeDob_${idx}`]}
                        helperText={touched[`nomineeDob_${idx}`] ? errors[`nomineeDob_${idx}`] : "Select date of birth"}
                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#7522D1' },
                            '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl fullWidth size="small" error={touched[`nomineeRelation_${idx}`] && !!errors[`nomineeRelation_${idx}`]}>
                        <InputLabel id={`nominee-relation-label-${idx}`} sx={{ '&.Mui-focused': { color: '#7522D1' } }}>Relation</InputLabel>
                        <Select
                          labelId={`nominee-relation-label-${idx}`}
                          value={nominee.relation || ''}
                          label="Relation"
                          onChange={(e) => handleNomineeChange(idx, 'relation', e.target.value)}
                          sx={{
                            borderRadius: 3,
                            bgcolor: '#F8FAFC',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7522D1' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7522D1' }
                          }}
                        >
                          <MenuItem value="Father" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Father')}>Father</MenuItem>
                          <MenuItem value="Mother" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Mother')}>Mother</MenuItem>
                          <MenuItem value="Spouse" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Spouse')}>Spouse</MenuItem>
                          <MenuItem value="Sibling">Sibling</MenuItem>
                          <MenuItem value="Child">Child</MenuItem>
                        </Select>
                        {touched[`nomineeRelation_${idx}`] && errors[`nomineeRelation_${idx}`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            {errors[`nomineeRelation_${idx}`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Nominee Aadhaar Number"
                        size="small"
                        fullWidth
                        value={nominee.aadhaarNumber || ''}
                        onChange={(e) => handleNomineeChange(idx, 'aadhaarNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
                        error={touched[`nomineeAadhaarNumber_${idx}`] && !!errors[`nomineeAadhaarNumber_${idx}`]}
                        helperText={touched[`nomineeAadhaarNumber_${idx}`] ? errors[`nomineeAadhaarNumber_${idx}`] : "Enter 12-digit Aadhaar number"}
                        inputProps={{ maxLength: 12 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#F8FAFC',
                            '& fieldset': { borderColor: '#E2E8F0' },
                            '&:hover fieldset': { borderColor: '#7522D1' },
                            '&.Mui-focused fieldset': { borderColor: '#7522D1' }
                          }
                        }}
                      />
                    </Grid>

                    {/* Empty spacing grid column to align the Aadhaar copies to next row nicely */}
                    <Grid item size={{ xs: 0, md: 4 }} />

                    {/* Aadhaar Front/Back Copy Upload boxes */}
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          border: '1px dashed #CBD5E1',
                          bgcolor: '#F8FAFC',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#7522D1',
                            bgcolor: alpha('#7522D1', 0.01)
                          }
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} color="#475569" display="block" sx={{ mb: 1.5 }}>
                          Aadhaar Card Front Copy *
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              borderColor: '#7522D1',
                              color: '#7522D1',
                              fontWeight: 600,
                              px: 2,
                              '&:hover': {
                                bgcolor: alpha('#7522D1', 0.05),
                                borderColor: '#7522D1'
                              }
                            }}
                          >
                            Upload Front
                            <input
                              type="file"
                              hidden
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleNomineeChange(idx, 'aadhaarFrontFile', e.target.files[0]);
                                }
                              }}
                            />
                          </Button>
                          {nominee.aadhaarFrontFile && (
                            <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="success.main">
                              {nominee.aadhaarFrontFile.name}
                            </Typography>
                          )}
                          {!nominee.aadhaarFrontFile && nominee.aadhaarFrontPath && (
                            <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="primary.main">
                              Uploaded & Secure
                            </Typography>
                          )}
                        </Stack>
                        {touched[`nomineeAadhaarFront_${idx}`] && errors[`nomineeAadhaarFront_${idx}`] && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                            {errors[`nomineeAadhaarFront_${idx}`]}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          border: '1px dashed #CBD5E1',
                          bgcolor: '#F8FAFC',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#7522D1',
                            bgcolor: alpha('#7522D1', 0.01)
                          }
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} color="#475569" display="block" sx={{ mb: 1.5 }}>
                          Aadhaar Card Back Copy *
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            sx={{
                              textTransform: 'none',
                              borderRadius: 2,
                              borderColor: '#7522D1',
                              color: '#7522D1',
                              fontWeight: 600,
                              px: 2,
                              '&:hover': {
                                bgcolor: alpha('#7522D1', 0.05),
                                borderColor: '#7522D1'
                              }
                            }}
                          >
                            Upload Back
                            <input
                              type="file"
                              hidden
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleNomineeChange(idx, 'aadhaarBackFile', e.target.files[0]);
                                }
                              }}
                            />
                          </Button>
                          {nominee.aadhaarBackFile && (
                            <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="success.main">
                              {nominee.aadhaarBackFile.name}
                            </Typography>
                          )}
                          {!nominee.aadhaarBackFile && nominee.aadhaarBackPath && (
                            <Typography variant="caption" noWrap sx={{ maxWidth: 180, fontWeight: 600 }} color="primary.main">
                              Uploaded & Secure
                            </Typography>
                          )}
                        </Stack>
                        {touched[`nomineeAadhaarBack_${idx}`] && errors[`nomineeAadhaarBack_${idx}`] && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                            {errors[`nomineeAadhaarBack_${idx}`]}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </CardContent>
          </Card>
        </Box>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={currentFieldObj?.accept || ".pdf"}
        style={{ display: 'none' }}
      />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
        <Button variant="outlined" onClick={handleBack} sx={{ minWidth: 120 }}>
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleSkip} sx={{ minWidth: 120 }}>
            Skip for now
          </Button>
          <Button variant="contained" onClick={handleNext} sx={{ minWidth: 120 }} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (employeeId ? "Update" : "Save & Next")}
          </Button>
        </Box>
      </Box>
      <Notification />
    </Box>
  );
};

export default JoiningForms;
